import React from "react";
import { Hero, PageContent } from "../../components";

function PlantSynheticBiology() {
  return (
    <div className="flex overflow-x-hidden overflow-y-hidden relative flex-col -z-10">
      <Hero title={"Plant Synthetic Biology"}>
        Plant science unveils nature's evolutionary wonders, showcasing the
        adaptability and diversity of plant{" "}
        <span className="text-lg font-black text-VertPastel md:text-xl lg:text-2xl">
          life.
        </span>
      </Hero>

      <PageContent>
        {`
We are confident that our project can be considered one of the best in plant synthetic biology, because we have succeeded in developing and designing a new system for heat production that is both environmentally friendly and sustainable.

<div class="image_frame">
    <img class="illustration medium" src="https://static.igem.wiki/teams/4577/wiki/project/plant-synthetic-bio/plant-synthetic-biology.jpg">
</div>

# Our Project

In view of the global environmental crisis, we have identified an immediate need to develop clean, green energy systems.

Our project is to develop a new low environmental impact heat production system using a green microalga known as *Chlamydomonas reinhardtii*.

## Our chassis

![]()

<div class="image_frame">
    <img class="illustration small" src="https://static.igem.wiki/teams/4577/wiki/project/plant-synthetic-bio/chlamy-parle-removebg-preview-1.png">
</div>

*Chlamydomonas reinhardtii*, also known as Chlamy, is a unicellular green alga that is now well known to the scientific community, since its genome was sequenced at the beginning of the 21st century [1]. It is a reference organism that has been widely studied for its rapid growth and high biomass production. It is a photoautotrophic organism that is able to survive in difficult conditions. Moreover, recent scientific advances have made genome manipulation more accessible

<div class="image_frame">
    <img class="illustration medium" src="https://static.igem.wiki/teams/4577/wiki/project/plant-synthetic-bio/chlamy-microscope.jpg">
    <p>Microscopic photograph of <i>Chlamydomonas reinhardtii</i> (source : Encyclopedia of Life)</p>
</div>

![]()

### Our Heat-Producing Protein: Alternative Oxidase (AOX)
*C. reinhardtii* was chosen as the chassis, since the alternative oxidase (AOX) is involved within the mitochondrial chain. It is an alternative pathway that is involved when there is an imbalance in the mitochondrial chain, and which in fine leads to heat production. AOX undergoes post-translational modifications before becoming active. This is also one of the reasons why we focused on *Chlamydomonas reinhardtii*, since bacteria do not have post-translational regulatory mechanisms. Pyruvates can also be added to stabilize the active form of the protein. Furthermore, we appreciated its ability to live in a variety of environments, such as wastewater and synthetic urine, since it is able to grow in pH conditions ranging from 2.5 to 7, maintaining a neutral intracellular pH. It is also known for its tolerance and ability to bioremediate and decontaminate heavy metals.

## The Genetic Modification
Chlamy is the ideal host for producing various molecules of interest. However, it has one major limitation: we have always had a low level of nuclear transgene expression. This problem seems to be principally due to gene silencing, a widespread process among plant species. In *C. reinhardtii*, this mechanism is often associated with inadequate transgene design. To balance out this low level of expression, several techniques have been developed. Firstly, the promoter has a key role to play. That's why in our construct we opted for the PSAD promoter, which seems to allow a high level of expression of the constitutively expressed genes. We then optimized the codons of the sraox gene to adapt it to its host and metabolic pathways. We then added the introns of the *rbsc2* gene to prevent gene silencing. The first intron of the *rbsc2* gene is essential as it contains an enhancement sequence [2]. In addition, we added a signal peptide, attached to the gene of interest, to ensure that the AOX protein is directed to the mitochondrial membrane.

<div class="image_frame">
    <img class="illustration medium" src="https://static.igem.wiki/teams/4577/wiki/project/plant-synthetic-bio/plasmid-chlamy.png">
</div>

To build the transgene, we used the Chlamydomonas Modular Cloning (MoClo) kit, based on Golden Gate cloning technologies. This kit is available from the Chlamydomonas Resource Center and contains various promoters, sequences, tags and untranslated regions (https://www.chlamycollection.org/). All these resources have enabled us to construct an ideal transgene for chlamy.
###  Results
Finally, we succeeded in modifying genetically not only the wild-type strain of *Chlamydomonas reinhardtii*, but also mutant strains at the mitochondrial chain level to force the alternative pathway that even produces AOX. You can read more about our heat production results here. 

<div class="image_frame">
    <img class="illustration medium" src="https://static.igem.wiki/teams/4577/wiki/project/plant-synthetic-bio/coculture-tap.jpg">
    <p>Preview of heat production with transfected <i>Chlamydomonas reinhardtii</i> wildtype after addition of pyruvate</p>
</div>

In the same way, we succeeded in adapting chlamy to survive in synthetic wastewater. We also tried with synthetic urine, but had more difficulty. You can see our results on microorganism adaptation ([here](https://2023.igem.wiki/ionis-paris/project/results)

<div class="image_frame">
    <img class="illustration medium" src="https://static.igem.wiki/teams/4577/wiki/project/plant-synthetic-bio/chlamy-wastewater.jpg">
    <p>Preview of adaptation of Chlamydomonas reinhardtii in synthetic wastewater</p>
</div>

## Sources
1. Merchant, Sabeeha S. et al. 2007. « The Chlamydomonas Genome Reveals the Evolution of Key Animal and Plant Functions ». Science (New York, N.Y.) 318(5848): 245‑50.
2. Schroda, Michael. 2019. « Good News for Nuclear Transgene Expression in Chlamydomonas ». Cells 8(12): 1534.
        `}
      </PageContent>
    </div>
  );
}

export default PlantSynheticBiology;
